import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Budget {
  id: string
  name: string
  department: string
  fiscal_year: number
  total_amount: number
  allocated: number
  spent: number
  remaining: number
  status: string
}

interface LineItem {
  id: string
  budget_name: string
  category: string
  description: string
  planned_amount: number
  actual_amount: number
  variance: number
  status: string
}

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState<Budget[]>([])
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [budgetsRes, itemsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/budgets/'),
      fetch(API_BASE + '/budgets/line-items'),
      fetch(API_BASE + '/budgets/statistics')
    ])
    if (budgetsRes.ok) setBudgets((await budgetsRes.json()).budgets || [])
    if (itemsRes.ok) setLineItems((await itemsRes.json()).line_items || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': case 'on_track': return 'bg-green-100 text-green-800'
      case 'pending': case 'under_review': return 'bg-yellow-100 text-yellow-800'
      case 'over_budget': return 'bg-red-100 text-red-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  const getUtilization = (budget: Budget) => {
    return Math.round((budget.spent / budget.total_amount) * 100)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Budget Planning</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Budget</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Budgets</div>
              <div className="text-2xl font-bold">{statistics.total_budgets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Planned</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_planned)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Spent</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.total_spent)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Remaining</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_remaining)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Utilization</div>
              <div className="text-2xl font-bold">{statistics.avg_utilization}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Budgets</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allocated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {budgets.slice(0, 10).map(budget => (
                  <tr key={budget.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{budget.name}</div>
                      <div className="text-xs text-gray-500">{budget.department} • FY{budget.fiscal_year}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{formatCurrency(budget.total_amount)}</div>
                      <div className="text-xs text-gray-500">Spent: {formatCurrency(budget.spent)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getUtilization(budget) > 90 ? 'bg-red-500' : 'bg-blue-600'}`}
                            style={{ width: `${Math.min(getUtilization(budget), 100)}%` }}
                          />
                        </div>
                        <span className="text-sm">{getUtilization(budget)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(budget.status)}`}>
                        {budget.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Line Items</h3>
            <div className="space-y-3">
              {lineItems.slice(0, 10).map(item => (
                <div key={item.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{item.description}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(item.status)}`}>{item.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{item.budget_name} • {item.category}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-bold">{formatCurrency(item.planned_amount)}</span>
                    <span className={`text-xs ${item.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {item.variance >= 0 ? '+' : ''}{formatCurrency(item.variance)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
